import { useCallback, useEffect, useRef } from "react";
import type { HubAgent } from "../lib/api";

/** What an Agent page in a frame says to the Hub. */
export type AgentFrameMessage = { type?: unknown; [key: string]: unknown };

/**
 * The Hub's side of one Agent page it keeps in a frame: the workspace, the
 * settings, the side-by-side view.
 *
 * The page is told whether it can be seen -- a hidden one releases its stream
 * and pauses its media -- and, while it is seen, where to be. It is told when
 * either changes, when the frame loads, and again when the page says it can
 * hear (`pa.studio.mounted`): anything sent before that reached nobody. What
 * else the page says is handed to `onMessage`, pass a stable one; its steps
 * are the Hub's to take (`useStudioNavigation`).
 *
 * The frames themselves stay where they are kept -- one per visited Agent, a
 * hidden one holding its drafts -- because that is each page's own business.
 */
export function useAgentFrame({ visible, route, agent, onMessage }: {
  visible: boolean;
  /** The Hub's current directory row; a read-only projection into this Agent's document. */
  agent?: HubAgent;
  /** Where the page should be while it is seen. */
  route?: string;
  onMessage?: (data: AgentFrameMessage) => void;
}) {
  const ref = useRef<HTMLIFrameElement>(null);
  const tell = useCallback(() => {
    const page = ref.current?.contentWindow;
    page?.postMessage({ type: "pa.workspace.visibility", visible }, location.origin);
    if (visible && route) page?.postMessage({ type: "pa.studio.route", hash: route }, location.origin);
  }, [visible, route]);
  const tellAgent = useCallback(() => {
    if (agent) ref.current?.contentWindow?.postMessage({ type: "pa.workspace.agent", instance: agent }, location.origin);
  }, [agent]);
  useEffect(tell, [tell]);
  useEffect(tellAgent, [tellAgent]);
  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.origin !== location.origin || !ref.current || event.source !== ref.current.contentWindow) return;
      if (event.data?.type === "pa.studio.mounted") { tell(); tellAgent(); }
      onMessage?.(event.data ?? {});
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [tell, tellAgent, onMessage]);
  return { ref, onLoad: () => { tell(); tellAgent(); } };
}
